import { PROFILE_ACTION, IProfileActionService } from "../IProfileActionService";
import { Injectable } from "@angular/core";
import { CubiqWorker } from "../../worker/CubiqWorker";
import {
  PRIMITIVE,
  ServiceMessageBrokerFactory,
  ServiceMessageBroker,
} from "shared/provider-webworker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { PromiseSet } from "../../promise/PromiseSet";
import { PrimaryPromiseSet } from "shared/auth-services";
import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { ProfileSettingImpl } from "shared/cubiq-model-multi-asset/model/impl/ProfileSettingImpl";
import { Subject, Observable } from "rxjs";
import { ActiveUser } from "../../model/core/ActiveUser";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ProfileSettingKeys } from "../../model/core/ProfileSettingKeys";
import { SystemConfigPrimaryService } from "shared/auth-services";

//used to filter out user settings required keys
export const USER_SETTINGS_VALUE_KEYS = [
  ProfileSettingKeys.userProfileSettingsLadderOrdersPreferences,
  ProfileSettingKeys.userProfileSettingsTradeBlotterPreferences,
  ProfileSettingKeys.userProfileSettingsOrderBlotterTemperatures,
  ProfileSettingKeys.userProfileSettingsOrderBlotterPreferences,
  ProfileSettingKeys.userProfileSettingsNotifPopup,
  ProfileSettingKeys.userProfileSettingsNotifApp,
  ProfileSettingKeys.userThemeSettings,
  ProfileSettingKeys.userZoomSetting,
  ProfileSettingKeys.userOrderEntrySpotSettings,
  ProfileSettingKeys.userOrderEntryNdfSettings,
  ProfileSettingKeys.userOrderEntryAlgoSettings,
  ProfileSettingKeys.userOrderEntryPreferencesSettings,
  ProfileSettingKeys.userOrderEntryActiveTabsSettings,
  ProfileSettingKeys.userOrderEntryNDFAlgoSettings,
];

@Injectable()
export class ProfilePrimaryActionService implements IProfileActionService {
  protected VALUE: CubiqTS.ConcreteEncodable;

  protected primed: PromiseSet<boolean> = new PrimaryPromiseSet<boolean>();
  protected broker: ServiceMessageBroker;
  protected profileSettings: any = {};
  private settingsPrimed: boolean = false;
  private _updateSubjectMap: Map<string, Set<Subject<any>>> = new Map();
  private _updateSubjectPortMap: Map<Subject<any>, MessagePort> = new Map();

  constructor(
    private worker: CubiqWorker,
    private activeUserService: ActiveUserProfilePromiseService,
    protected factory: ServiceMessageBrokerFactory,
    public systemSettingService: SystemConfigPrimaryService,
  ) {
    this.VALUE = CubiqTS.ENCODABLE(CubiqTS.ProfileUpdateRequestImpl);

    this.broker = this.factory.createMessageBroker(PROFILE_ACTION, true, true);
    this.broker.registerMethod(
      "registerReplicaUpdateSubject",
      [PRIMITIVE, PRIMITIVE],
      this.registerReplicaUpdateSubject.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "updateProfile",
      [PRIMITIVE, PRIMITIVE],
      this.updateProfile.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getProfileConfig",
      [PRIMITIVE],
      this.getProfileConfig.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "getProfileSettings",
      [],
      this.getProfileSettings.bind(this),
      PRIMITIVE,
    );
    this.broker.registerMethod(
      "createUserSettingsProfileUpdateRequest",
      [],
      this.createUserSettingsProfileUpdateRequest.bind(this),
      this.VALUE,
    );
    this.broker.registerMethod("isPrimed", [], this.isPrimed.bind(this), PRIMITIVE);
    worker.isAuth(`ProfilePrimaryActionService`).then(() => {
      this.worker
        .primeStaticData<CubiqTS.ProfileSettingImpl>(CubiqTS.ProfileSettingImpl.name)
        .subscribe(
          (profileSettings: CubiqTS.ProfileSettingImpl[]) => {
            profileSettings.forEach((p) => {
              this.profileSettings[p.profileSettingKey] = p.profileSettingValue;
            });

            this.settingsPrimed = true;

            this.activeUserService.getActiveUserData().then((activeUser: ActiveUser) => {
              this.primed.resolve(true);
            });
          },
          (error) => {
            if (!this.settingsPrimed)
              logger.out.error("ProfilePrimaryActionService: profile settings prime failed");
          },
        );
    });
  }

  protected registerReplicaUpdateSubject(settingKey: string, port: MessagePort): void {
    let subject = new Subject<string>();

    if (!this._updateSubjectMap.has(settingKey)) {
      this._updateSubjectMap.set(settingKey, new Set());
    }

    this._updateSubjectMap.get(settingKey).add(subject);
    // objects as keys
    this._updateSubjectPortMap.set(subject, port);

    subject.subscribe(
      (profileValue: string) => {
        let port = this._updateSubjectPortMap.get(subject);

        port.postMessage({
          profileValue: profileValue,
        });
      },
      (err) => {
        if (err) {
          logger.out.error(`ProfilePrimaryActionService: failed to remote ${settingKey}`);
        }
      },
    );
  }

  getProfileConfigUpdates(profileSettingKey: string): Observable<string> {
    let subject = new Subject<string>();

    if (!this._updateSubjectMap.has(profileSettingKey)) {
      this._updateSubjectMap.set(profileSettingKey, new Set());
    }

    this._updateSubjectMap.get(profileSettingKey).add(subject);

    return subject.asObservable();
  }

  updateProfile(
    profileSettings: Array<CubiqTS.ProfileSettingImpl>,
    isUserSettings: boolean,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (profileSettings && Array.isArray(profileSettings) && this.settingsPrimed) {
        let request: CubiqTS.ProfileUpdateRequestImpl = new CubiqTS.ProfileUpdateRequestImpl();
        request.deltaRequestFg = true; //flag used for partial updates

        let userPreferencesValue: string = null;
        request.settings = [];
        profileSettings.forEach((setting) => {
          let profileSetting: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
          profileSetting.profileSettingKey = setting.profileSettingKey;
          profileSetting.profileSettingValue = setting.profileSettingValue;
          if (setting.profileSettingKey !== ProfileSettingKeys.userPreferences) {
            if (
              this.profileSettings &&
              this.profileSettings.hasOwnProperty(setting.profileSettingKey)
            ) {
              if (
                this.profileSettingValueIsModified(
                  profileSetting.profileSettingKey,
                  profileSetting.profileSettingValue,
                )
              )
                request.settings.push(profileSetting);
            } else {
              request.settings.push(profileSetting);
            }
          } else {
            userPreferencesValue = setting.profileSettingValue;
          }
        });

        if (isUserSettings && userPreferencesValue !== null) {
          // TODO consider use of  request from bfx-user-seetings directly
          // JSON roundtrip may be redundant without worker
          request.updateUserPreferences = true;
          this.populateUserPreferences(request, userPreferencesValue);
        }
        if (request.updateUserPreferences || (request.settings && request.settings.length > 0)) {
          logger.out.info(
            `${this.constructor.name}: saving : ` +
              `${request.settings.length} settings modified & ` +
              `request.updateUserPreferences:${request.updateUserPreferences}`,
          );
          this.saveProfile(request)
            .then(() => {
              resolve();
            })
            .catch((error: any) => {
              reject(error);
            });
        } else {
          logger.out.info(
            `${this.constructor.name}: no saving required : ` +
              `${request.settings.length} settings modified & ` +
              `request.updateUserPreferences:${request.updateUserPreferences}`,
          );
          resolve();
        }
      }
    });
  }

  createUserSettingsProfileUpdateRequest(): Promise<CubiqTS.ProfileUpdateRequestImpl> {
    let request: CubiqTS.ProfileUpdateRequestImpl = new CubiqTS.ProfileUpdateRequestImpl();
    request.settings = this.createUserSettingsProfileSettingsList();

    return new Promise<CubiqTS.ProfileUpdateRequestImpl>((resolve, reject) => {
      resolve(request);
    });
  }

  private createUserSettingsProfileSettingsList(): Array<CubiqTS.ProfileSetting> {
    let profile = Object.assign(new Object(), this.profileSettings);
    let profileSettingList: Array<CubiqTS.ProfileSetting> = [];

    Object.keys(profile).forEach((p) => {
      if (USER_SETTINGS_VALUE_KEYS.indexOf(p) >= 0) {
        let setting: CubiqTS.ProfileSettingImpl = new CubiqTS.ProfileSettingImpl();
        setting.profileSettingKey = p;
        setting.profileSettingValue = profile[p];
        profileSettingList.push(setting);
      }
    });

    return profileSettingList;
  }

  // if settingsKey is set, only send its update
  protected _saveProfile(
    request: CubiqTS.ProfileUpdateRequestImpl,
    settingKey: string = null,
  ): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      request.settings = request.settings.filter((setting) => {
        let isValid =
          setting.profileSettingKey == Object(setting.profileSettingKey) &&
          setting.profileSettingValue == Object(setting.profileSettingValue);

        if (!isValid && this._updateSubjectMap.has(setting.profileSettingKey)) {
          logger.out.error(
            `${this.constructor.name}: dropping invalid ProfileSetting` +
              ` for registered key ${setting.profileSettingKey}: %O`,
            setting,
          );
        } else if (!isValid) {
          logger.out.warn(`${this.constructor.name}: dropping invalid ProfileSetting: %O`, setting);
        }

        return isValid;
      });

      this.worker.postServiceRequest(request).then(
        (payload: any) => {
          // this.profileSettings = {};
          request.settings.forEach((value: ProfileSettingImpl) => {
            this.profileSettings[value.profileSettingKey] = value.profileSettingValue;

            if (this._updateSubjectMap.has(value.profileSettingKey)) {
              let subjects = this._updateSubjectMap.get(value.profileSettingKey);
              subjects.forEach((subject) => {
                subject.next(value.profileSettingValue);
              });
            }
          });
          resolve(payload);
        },
        (error: any) => {
          reject(error);
        },
      );
    });
  }

  private saveProfile(request: CubiqTS.ProfileUpdateRequestImpl): Promise<void> {
    return this._saveProfile(request);
  }

  getProfileConfig(settingKey: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      this.primed.get().then(
        () => {
          resolve(this.profileSettings[settingKey]);
        },
        (reason) => {
          logger.out.info(`getSystemPrfile promise rejection:` + settingKey + " : " + reason);
          reject(reason);
        },
      );
    });
  }

  getProfileSettings(): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.primed.get().then(() => {
        resolve(this.profileSettings);
      });
    });
  }

  private profileSettingValueIsModified(
    profileSettingKey: string,
    profileSettingValue: string,
  ): boolean {
    return this.profileSettings[profileSettingKey] !== profileSettingValue;
  }

  private populateUserPreferences(
    request: CubiqTS.ProfileUpdateRequestImpl,
    userPreferencesValue: string,
  ) {
    if (userPreferencesValue != null) {
      try {
        let userPref: any = JSON.parse(userPreferencesValue);
        if (
          userPref.hasOwnProperty("defaultPositionBookId") &&
          userPref.defaultPositionBookId != null
        )
          request.defaultPositionBookId = CubiqTS.Long.fromString(userPref.defaultPositionBookId);
        if (userPref.hasOwnProperty("emailAddress") && userPref.emailAddress != null)
          request.emailAddress = userPref.emailAddress;
        if (userPref.hasOwnProperty("smsNumber") && userPref.smsNumber != null)
          request.smsNumber = userPref.smsNumber;
        if (userPref.hasOwnProperty("sendEmailAnyOrderIFillFg"))
          request.sendEmailAnyOrderIFillFg = userPref.sendEmailAnyOrderIFillFg;
        if (userPref.hasOwnProperty("sendEmailMyOrdEnteredFg"))
          request.sendEmailMyOrdEnteredFg = userPref.sendEmailMyOrdEnteredFg;
        if (userPref.hasOwnProperty("sendEmailMyOrdExpiryFg"))
          request.sendEmailMyOrdExpiryFg = userPref.sendEmailMyOrdExpiryFg;
        if (userPref.hasOwnProperty("sendEmailMyOrdFillFg"))
          request.sendEmailMyOrdFillFg = userPref.sendEmailMyOrdFillFg;
        if (userPref.hasOwnProperty("sendSMSAnyOrderIFillFg"))
          request.sendSMSAnyOrderIFillFg = userPref.sendSMSAnyOrderIFillFg;
        if (userPref.hasOwnProperty("sendSMSMyOrdEnteredFg"))
          request.sendSMSMyOrdEnteredFg = userPref.sendSMSMyOrdEnteredFg;
        if (userPref.hasOwnProperty("sendSMSMyOrdExpiryFg"))
          request.sendSMSMyOrdExpiryFg = userPref.sendSMSMyOrdExpiryFg;
        if (userPref.hasOwnProperty("sendSMSMyOrdFillFg"))
          request.sendSMSMyOrdFillFg = userPref.sendSMSMyOrdFillFg;
        if (userPref.hasOwnProperty("sendEmailMyOrdModifyFg"))
          request.sendEmailMyOrdModifyFg = userPref.sendEmailMyOrdModifyFg;
        if (userPref.hasOwnProperty("sendEmailMyOrdCancelFg"))
          request.sendEmailMyOrdCancelFg = userPref.sendEmailMyOrdCancelFg;
        if (userPref.hasOwnProperty("sendSMSMyOrdModifyFg"))
          request.sendSMSMyOrdModifyFg = userPref.sendSMSMyOrdModifyFg;
        if (userPref.hasOwnProperty("sendSMSMyOrdCancelFg"))
          request.sendSMSMyOrdCancelFg = userPref.sendSMSMyOrdCancelFg;
      } catch (e) {
        logger.out.warn(`${this.constructor.name}: Error populating user prefernces: %O`, e);
      }
    }
  }

  public isPrimed(): Promise<boolean> {
    return this.primed.get();
  }
}
